import React, { useState, useEffect, useCallback, useRef } from 'react';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageSquare, Plus, Edit, Trash2, Copy, Search, RefreshCw, 
  Loader2, X, Tag, Star, StarOff, Clock, Variable
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Available dynamic variables
const DYNAMIC_VARIABLES = [
  { name: 'prenom', label: 'Prénom', description: 'Prénom du destinataire' },
  { name: 'nom', label: 'Nom', description: 'Nom du destinataire' },
  { name: 'nom_complet', label: 'Nom complet', description: 'Prénom + Nom' },
  { name: 'telephone', label: 'Téléphone', description: 'Numéro de téléphone' },
  { name: 'email', label: 'Email', description: 'Adresse email' },
  { name: 'date', label: 'Date', description: 'Date du jour (JJ/MM/AAAA)' },
  { name: 'heure', label: 'Heure', description: 'Heure actuelle (HH:MM)' },
  { name: 'societe', label: 'Société', description: 'Nom de votre entreprise' },
  { name: 'code', label: 'Code', description: 'Code personnalisé' },
  { name: 'lien', label: 'Lien', description: 'URL personnalisée' },
  { name: 'montant', label: 'Montant', description: 'Montant (ex: commande)' },
  { name: 'ref', label: 'Référence', description: 'Numéro de référence' }
];

const categoryColors = {
  marketing: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  transactional: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  notification: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  reminder: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  confirmation: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  other: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' }
};

const categoryLabels = {
  marketing: 'Marketing',
  transactional: 'Transactionnel',
  notification: 'Notification',
  reminder: 'Rappel',
  confirmation: 'Confirmation',
  other: 'Autre'
};

const SMSTemplates = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('marketing');
  const [formFavorite, setFormFavorite] = useState(false);
  
  // Variable autocomplete states
  const [showVariables, setShowVariables] = useState(false);
  const [variableFilter, setVariableFilter] = useState('');
  const textareaRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Insert variable at cursor position
  const insertVariable = (varName) => {
    const variable = `{${varName}}`;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = formContent.substring(0, start) + variable + formContent.substring(end);
      setFormContent(newContent);
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setFormContent(formContent + variable);
    }
    setShowVariables(false);
    setVariableFilter('');
  };

  // Filter variables based on search
  const filteredVariables = DYNAMIC_VARIABLES.filter(v => 
    v.name.toLowerCase().includes(variableFilter.toLowerCase()) ||
    v.label.toLowerCase().includes(variableFilter.toLowerCase())
  );

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sms-mailing/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let data = response.data || [];
      
      // Filter by search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        data = data.filter(t => 
          t.name?.toLowerCase().includes(query) ||
          t.content?.toLowerCase().includes(query)
        );
      }
      
      // Filter by category
      if (filterCategory !== 'all') {
        data = data.filter(t => t.category === filterCategory);
      }
      
      // Sort: favorites first, then by name
      data.sort((a, b) => {
        if (a.favorite && !b.favorite) return -1;
        if (!a.favorite && b.favorite) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Initialize with empty if endpoint doesn't exist yet
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, filterCategory]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Calculate SMS metrics
  const calculateSmsMetrics = (text) => {
    if (!text) return { chars: 0, smsCount: 0 };
    const chars = text.length;
    const smsCount = chars <= 160 ? 1 : Math.ceil(chars / 153);
    return { chars, smsCount };
  };

  // Open modal for new template
  const handleNew = () => {
    setEditingTemplate(null);
    setFormName('');
    setFormContent('');
    setFormCategory('marketing');
    setFormFavorite(false);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormName(template.name || '');
    setFormContent(template.content || '');
    setFormCategory(template.category || 'marketing');
    setFormFavorite(template.favorite || false);
    setShowModal(true);
  };

  // Save template
  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }
    if (!formContent.trim()) {
      toast.error('Veuillez entrer le contenu du message');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        name: formName.trim(),
        content: formContent.trim(),
        category: formCategory,
        favorite: formFavorite
      };
      
      if (editingTemplate) {
        await axios.put(`${API_URL}/sms-mailing/templates/${editingTemplate.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Modèle mis à jour');
      } else {
        await axios.post(`${API_URL}/sms-mailing/templates`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Modèle créé');
      }
      
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    
    try {
      await axios.delete(`${API_URL}/sms-mailing/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modèle supprimé');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle favorite
  const handleToggleFavorite = async (template) => {
    try {
      await axios.put(`${API_URL}/sms-mailing/templates/${template.id}`, {
        ...template,
        favorite: !template.favorite
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Copy to clipboard
  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copié dans le presse-papier');
  };

  const metrics = calculateSmsMetrics(formContent);

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Messages prédéfinis
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Créez et gérez vos modèles de messages SMS
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchTemplates}
              disabled={loading}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button onClick={handleNew} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau modèle
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Rechercher un modèle..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-9 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              </div>
              
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className={`w-[180px] ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                  <Tag className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="transactional">Transactionnel</SelectItem>
                  <SelectItem value="notification">Notification</SelectItem>
                  <SelectItem value="reminder">Rappel</SelectItem>
                  <SelectItem value="confirmation">Confirmation</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
              
              {(searchQuery || filterCategory !== 'all') && (
                <Button
                  variant="ghost"
                  onClick={() => { setSearchQuery(''); setFilterCategory('all'); }}
                  className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                >
                  <X className="h-4 w-4 mr-2" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-500" />
          </div>
        ) : templates.length === 0 ? (
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className={`text-center py-16 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="font-medium text-lg mb-2">Aucun modèle de message</p>
              <p className="mb-6">Créez votre premier modèle pour gagner du temps lors de vos campagnes</p>
              <Button onClick={handleNew} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4 mr-2" />
                Créer un modèle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => {
              const templateMetrics = calculateSmsMetrics(template.content);
              const colors = categoryColors[template.category] || categoryColors.other;
              
              return (
                <Card 
                  key={template.id} 
                  className={`group transition-all ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)] hover:border-indigo-500/50' : 'hover:border-indigo-300'}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {template.favorite && (
                            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                          )}
                          <CardTitle className={`text-base ${isDarkMode ? 'text-white' : ''}`}>
                            {template.name}
                          </CardTitle>
                        </div>
                        <Badge className={`${colors.bg} ${colors.text} ${colors.border} border`}>
                          {categoryLabels[template.category] || template.category}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleFavorite(template)}
                        className={isDarkMode ? 'text-slate-400 hover:text-amber-500' : 'text-slate-400 hover:text-amber-500'}
                      >
                        {template.favorite ? (
                          <Star className="h-4 w-4 fill-current" />
                        ) : (
                          <StarOff className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-sm mb-4 line-clamp-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      {template.content}
                    </p>
                    
                    <div className={`flex items-center justify-between text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                      <span>{templateMetrics.chars} car. • {templateMetrics.smsCount} SMS</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-dashed ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(template.content)}
                        className={`flex-1 ${isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}`}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copier
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className={`max-w-xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
            <DialogHeader>
              <DialogTitle className={isDarkMode ? 'text-white' : ''}>
                {editingTemplate ? 'Modifier le modèle' : 'Nouveau modèle de message'}
              </DialogTitle>
              <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                Créez un modèle réutilisable pour vos campagnes SMS
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDarkMode ? 'text-slate-300' : ''}>Nom du modèle</Label>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Ex: Promotion été"
                    className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDarkMode ? 'text-slate-300' : ''}>Catégorie</Label>
                  <Select value={formCategory} onValueChange={setFormCategory}>
                    <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="transactional">Transactionnel</SelectItem>
                      <SelectItem value="notification">Notification</SelectItem>
                      <SelectItem value="reminder">Rappel</SelectItem>
                      <SelectItem value="confirmation">Confirmation</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={isDarkMode ? 'text-slate-300' : ''}>Message</Label>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {metrics.chars} caractères • {metrics.smsCount} SMS
                  </div>
                </div>
                
                {/* Textarea with variable button */}
                <div className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    placeholder="Saisissez votre message ici...&#10;&#10;Cliquez sur {x} pour insérer des variables"
                    rows={6}
                    className={`pr-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                  
                  {/* Variable button */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVariables(!showVariables)}
                    className={`absolute right-2 top-2 ${
                      isDarkMode 
                        ? 'text-indigo-400 hover:bg-[hsl(220,14%,22%)]' 
                        : 'text-indigo-600 hover:bg-indigo-50'
                    }`}
                    title="Insérer une variable"
                  >
                    <Variable className="h-4 w-4 mr-1" />
                    {'{x}'}
                  </Button>
                  
                  {/* Variables dropdown */}
                  {showVariables && (
                    <div className={`absolute right-0 top-12 w-72 rounded-lg shadow-xl border z-50 ${
                      isDarkMode 
                        ? 'bg-[hsl(220,14%,16%)] border-[hsl(220,14%,25%)]' 
                        : 'bg-white border-slate-200'
                    }`}>
                      <div className="p-2 border-b border-slate-200 dark:border-[hsl(220,14%,25%)]">
                        <Input
                          value={variableFilter}
                          onChange={(e) => setVariableFilter(e.target.value)}
                          placeholder="Rechercher..."
                          className={`h-8 text-sm ${isDarkMode ? 'bg-[hsl(220,14%,12%)] border-[hsl(220,14%,22%)] text-white' : ''}`}
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto p-1">
                        {filteredVariables.map((v) => (
                          <button
                            key={v.name}
                            type="button"
                            onClick={() => insertVariable(v.name)}
                            className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between transition-colors ${
                              isDarkMode 
                                ? 'hover:bg-[hsl(220,14%,22%)]' 
                                : 'hover:bg-slate-100'
                            }`}
                          >
                            <div>
                              <span className={`font-mono text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                {`{${v.name}}`}
                              </span>
                              <span className={`ml-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {v.label}
                              </span>
                            </div>
                          </button>
                        ))}
                        {filteredVariables.length === 0 && (
                          <p className={`text-sm text-center py-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Aucune variable trouvée
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Variables hint */}
                <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Astuce : Utilisez des variables pour personnaliser vos messages. Ex: Bonjour {'{prenom}'}, votre commande {'{ref}'} est prête.
                </p>
              </div>
              
              <div className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                <input
                  type="checkbox"
                  id="favorite"
                  checked={formFavorite}
                  onChange={(e) => setFormFavorite(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="favorite" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                  Marquer comme favori
                </Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingTemplate ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SMSLayout>
  );
};

export default SMSTemplates;
